import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Button } from '@/components/ui-kit/button';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { Plus } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeleteSkillSubCategory,
  useGetSkillSubCategories,
} from '../../hooks/use-skill-sub-categories';
import { DEFAULT_SKILL_SUB_CATEGORIES_ORDER } from '../../graphql/queries';
import {
  SkillSubCategoriesPagingResult,
  SkillSubCategory,
} from '../../types/skill-sub-category.types';
import { SkillSubCategoryFormDialog } from '../skill-sub-category-form-dialog/skill-sub-category-form-dialog';
import { createSkillSubCategoryTableColumns } from '../skill-sub-category-table-columns/skill-sub-category-table-columns';

/** "Skill Sub-categories" tab of the Skill Manager: the areas skills are grouped under (FrontEnd, QA, ...). */
export const SkillSubCategoryTab = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SKILL_SUB_CATEGORY.CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SKILL_SUB_CATEGORY.UPDATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.SKILL_SUB_CATEGORY.DELETE);

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_SKILL_SUB_CATEGORIES_ORDER,
  });
  const [formState, setFormState] = useState<{
    open: boolean;
    skillSubCategory: SkillSubCategory | null;
  }>({ open: false, skillSubCategory: null });
  const [deleteState, setDeleteState] = useState<{
    open: boolean;
    skillSubCategory: SkillSubCategory | null;
  }>({ open: false, skillSubCategory: null });

  const { data, isLoading, error } = useGetSkillSubCategories({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { SkillSubCategorys?: SkillSubCategoriesPagingResult })
    ?.SkillSubCategorys;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteSkillSubCategory, isPending: isDeleting } =
    useDeleteSkillSubCategory();

  const handleDeleteConfirm = async () => {
    if (!deleteState.skillSubCategory) return;
    await deleteSkillSubCategory(deleteState.skillSubCategory.ItemId);
    setDeleteState({ open: false, skillSubCategory: null });
  };

  const columns = createSkillSubCategoryTableColumns({
    t,
    onEdit: (skillSubCategory) => setFormState({ open: true, skillSubCategory }),
    onDelete: (skillSubCategory) => setDeleteState({ open: true, skillSubCategory }),
    canUpdate,
    canDelete,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        {canCreate && (
          <Button
            className="w-full sm:w-auto"
            onClick={() => setFormState({ open: true, skillSubCategory: null })}
          >
            <Plus className="size-4" />
            {t('CREATE_SKILL_SUB_CATEGORY')}
          </Button>
        )}
      </div>

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <SkillSubCategoryFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, skillSubCategory: open ? prev.skillSubCategory : null }))
        }
        skillSubCategory={formState.skillSubCategory}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, skillSubCategory: open ? deleteState.skillSubCategory : null })
        }
        title={t('DELETE_SKILL_SUB_CATEGORY')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
